import React, { useState } from 'react';
import { View, Text } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  Globe,
  FolderOpen,
  Lock,
  Star,
  DollarSign,
  TrendingUp,
  UserPlus,
  Rocket,
  Megaphone,
  Newspaper,
  Plus,
} from 'lucide-react-native';
import { useRouter } from 'expo-router';
import AnimatedPressable from '@/components/AnimatedPressable';
import { openExternalUrl } from '@/lib/linking';
import { useThemeColors } from '@/lib/useThemeColors';
import type { StartupUpdate, UpdateVisibility } from '@/lib/state/inverio-store';

type UpdateType = StartupUpdate['type'];

const VISIBILITY_LABELS: Record<UpdateVisibility, string> = {
  public: 'Public',
  data_room: 'Data Room',
  investors_only: 'Investors Only',
};

interface RecentUpdatesProps {
  updates: StartupUpdate[];
  /** Max number of items shown when collapsed */
  previewCount?: number;
  /** Show "Post Update" button */
  showPostButton?: boolean;
}

export default function RecentUpdates({
  updates,
  previewCount = 3,
  showPostButton = true,
}: RecentUpdatesProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);

  const sorted = [...updates].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const visible = expanded ? sorted : sorted.slice(0, previewCount);
  const hasMore = sorted.length > previewCount;

  const getUpdateIcon = (type: UpdateType, size = 16) => {
    switch (type) {
      case 'milestone': return <Star size={size} color={colors.success} />;
      case 'funding': return <DollarSign size={size} color={colors.success} />;
      case 'traction': return <TrendingUp size={size} color={colors.warning} />;
      case 'team': return <UserPlus size={size} color={colors.primary[600]} />;
      case 'product': return <Rocket size={size} color={colors.primary[600]} />;
      case 'announcement': return <Megaphone size={size} color={colors.primary[600]} />;
      default: return <Newspaper size={size} color={colors.text.tertiary} />;
    }
  };

  const getUpdateBgColor = (type: UpdateType) => {
    switch (type) {
      case 'milestone':
      case 'funding': return colors.traffic.greenLight;
      case 'traction': return colors.amber.light;
      default: return colors.primary[50];
    }
  };

  const getVisibilityIcon = (visibility: UpdateVisibility, size = 11) => {
    switch (visibility) {
      case 'public': return <Globe size={size} color={colors.text.tertiary} />;
      case 'data_room': return <FolderOpen size={size} color={colors.warning} />;
      case 'investors_only': return <Lock size={size} color={colors.primary[600]} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (updates.length === 0) {
    return (
      <View
        className="rounded-2xl p-5 items-center"
        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
      >
        <View
          className="w-11 h-11 rounded-full items-center justify-center mb-2.5"
          style={{ backgroundColor: colors.primary[50] }}
        >
          <Newspaper size={20} color={colors.primary[400]} />
        </View>
        <Text className="text-sm font-medium mb-1" style={{ color: colors.text.secondary }}>
          No updates yet
        </Text>
        {showPostButton && (
          <AnimatedPressable
            onPress={() => router.push('/(startup)/updates')}
            className="flex-row items-center gap-1.5 mt-2 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Plus size={14} color={colors.primary[600]} />
            <Text className="text-xs font-semibold" style={{ color: colors.primary[600] }}>
              Post First Update
            </Text>
          </AnimatedPressable>
        )}
      </View>
    );
  }

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
    >
      {/* Header — tap to expand/collapse */}
      <AnimatedPressable
        onPress={() => hasMore && setExpanded(!expanded)}
        className="flex-row items-center justify-between px-4 py-3"
        style={{ borderBottomWidth: 1, borderBottomColor: colors.border.light }}
      >
        <View className="flex-row items-center gap-2">
          <Newspaper size={16} color={colors.primary[600]} />
          <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
            Recent Updates
          </Text>
          <View className="px-1.5 py-0.5 rounded-full" style={{ backgroundColor: colors.primary[50] }}>
            <Text className="text-xs font-semibold" style={{ color: colors.primary[600] }}>
              {updates.length}
            </Text>
          </View>
        </View>
        <View className="flex-row items-center gap-2">
          {showPostButton && (
            <AnimatedPressable
              onPress={() => router.push('/(startup)/updates')}
              className="flex-row items-center gap-1 px-2.5 py-1 rounded-lg"
              style={{ backgroundColor: colors.primary[50] }}
            >
              <Plus size={12} color={colors.primary[600]} />
              <Text className="text-xs font-semibold" style={{ color: colors.primary[600] }}>New</Text>
            </AnimatedPressable>
          )}
          {hasMore && (
            expanded
              ? <ChevronUp size={18} color={colors.text.tertiary} />
              : <ChevronDown size={18} color={colors.text.tertiary} />
          )}
        </View>
      </AnimatedPressable>

      {/* Update items */}
      {visible.map((update, index) => (
        <Animated.View
          key={update.id}
          entering={expanded && index >= previewCount ? FadeInDown.delay((index - previewCount) * 50).duration(250) : undefined}
        >
          <AnimatedPressable
            onPress={() => {
              if (update.externalLink) openExternalUrl(update.externalLink, 'RecentUpdates.openLink');
            }}
            disabled={!update.externalLink}
            className={`flex-row items-start gap-3 px-4 py-3 ${index < visible.length - 1 ? 'border-b' : ''}`}
            style={{ borderColor: colors.border.light }}
          >
            <View
              className="w-8 h-8 rounded-full items-center justify-center mt-0.5"
              style={{ backgroundColor: getUpdateBgColor(update.type) }}
            >
              {getUpdateIcon(update.type)}
            </View>
            <View className="flex-1">
              <Text className="font-medium text-sm" style={{ color: colors.text.primary }} numberOfLines={1}>
                {update.title}
              </Text>
              <Text className="text-xs mt-0.5" style={{ color: colors.text.secondary }} numberOfLines={2}>
                {update.summary}
              </Text>
              <View className="flex-row items-center gap-3 mt-1.5">
                <View className="flex-row items-center gap-1">
                  <Clock size={10} color={colors.text.tertiary} />
                  <Text className="text-xs" style={{ color: colors.text.tertiary }}>
                    {formatDate(update.date)}
                  </Text>
                </View>
                {update.externalLink && (
                  <View className="flex-row items-center gap-1">
                    <ExternalLink size={10} color={colors.primary[600]} />
                    <Text className="text-xs" style={{ color: colors.primary[600] }}>Link</Text>
                  </View>
                )}
                <View className="flex-row items-center gap-1">
                  {getVisibilityIcon(update.visibility)}
                  <Text
                    className="text-xs"
                    style={{
                      color: update.visibility === 'public'
                        ? colors.text.tertiary
                        : update.visibility === 'data_room'
                          ? colors.warning
                          : colors.primary[600],
                    }}
                  >
                    {VISIBILITY_LABELS[update.visibility]}
                  </Text>
                </View>
              </View>
            </View>
          </AnimatedPressable>
        </Animated.View>
      ))}

      {/* Expand/collapse footer */}
      {hasMore && (
        <AnimatedPressable
          onPress={() => setExpanded(!expanded)}
          className="flex-row items-center justify-center gap-1.5 py-2.5"
          style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
        >
          <Text className="text-xs font-medium" style={{ color: colors.primary[600] }}>
            {expanded ? 'Show Less' : `Show ${sorted.length - previewCount} More`}
          </Text>
          {expanded
            ? <ChevronUp size={14} color={colors.primary[600]} />
            : <ChevronDown size={14} color={colors.primary[600]} />}
        </AnimatedPressable>
      )}
    </View>
  );
}
